/**
 * Wall faces in whole-property plan space: both faces of every straight wall
 * of a placed room, with the opening edges along each, and the measurements
 * along and across them that snapping and true-up both take.
 */
import { type FloorPlanRoom, type Point, roomPlacementRotateVector, roomPlacementTransform } from '@kaiizen/kai-floor-plan';
import { leafPoint, wallRuns } from '../room-geometry';

/** Directions this close to parallel (the sine of the angle between them) count as parallel; rotations are 5° steps. */
const PARALLEL_SIN = Math.sin(Math.PI / 180);

/** One face of a wall in whole-property plan space, with the opening edges along it. */
export interface SnapLine {
  face: 'inner' | 'outer';
  /** The face's start point. */
  origin: Point;
  /** Unit direction along the wall. */
  dir: Point;
  /** Unit normal across the wall, out of its room. */
  perp: Point;
  /** The face's length along `dir` from `origin`. */
  length: number;
  /** Where each opening (door, window, or plain opening) starts and ends along `dir` from `origin`. */
  openings: number[];
}

/** Both faces of every straight wall of a placed room, in whole-property plan space. */
export function roomSnapLines(room: FloorPlanRoom): SnapLine[] {
  const toPlan = roomPlacementTransform(room);
  return wallRuns(room).flatMap(run => {
    const dir = roomPlacementRotateVector(room, run.first.line.dir);
    const perp = roomPlacementRotateVector(room, run.first.line.perp);
    return (['inner', 'outer'] as const).map((face, side) => ({
      face,
      origin: toPlan(leafPoint(run.first, 0, side)),
      dir,
      perp,
      length: run.length,
      openings: run.openings,
    }));
  });
}

export const dot = (a: Point, b: Point): number => a.x * b.x + a.y * b.y;
export const add = (a: Point, b: Point): Point => ({ x: a.x + b.x, y: a.y + b.y });

/** Whether two faces run within a degree of parallel, either way. */
export const parallel = (a: SnapLine, b: SnapLine): boolean => Math.abs(a.dir.x * b.dir.y - a.dir.y * b.dir.x) < PARALLEL_SIN;
/** Whether two faces run within a degree of perpendicular. */
export const perpendicular = (a: SnapLine, b: SnapLine): boolean => Math.abs(dot(a.dir, b.dir)) < PARALLEL_SIN;

/** A point's coordinate along / across a face, from its origin. */
export const alongOn = (line: SnapLine, point: Point): number => dot({ x: point.x - line.origin.x, y: point.y - line.origin.y }, line.dir);
export const acrossOn = (line: SnapLine, point: Point): number => dot({ x: point.x - line.origin.x, y: point.y - line.origin.y }, line.perp);

/** The point `distance` along a face from its origin. */
export const pointAt = (line: SnapLine, distance: number): Point => ({ x: line.origin.x + line.dir.x * distance, y: line.origin.y + line.dir.y * distance });

/** How much of `partner` lies alongside `line` (ft): 0 or less when they don't overlap, as far apart as they are. */
export const overlapOf = (line: SnapLine, partner: SnapLine): number => {
  const from = alongOn(line, partner.origin), to = alongOn(line, pointAt(partner, partner.length));
  return Math.min(line.length, Math.max(from, to)) - Math.max(0, Math.min(from, to));
};
